import React from 'react';

import { ButtonBase, Typography } from '@mui/material';
import clsx from 'clsx';
import { ChevronLeft, SquarePen } from 'lucide-react';
import { useAlineMeasure } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { ChatThreadTitle } from './ChatThreadTitle';
import { $resetChatThreadsState } from '../chat-state';

const useStyles = makeStyles<{ hasBackButton: boolean; hasNewChatButton: boolean }>()(
  (theme, { hasBackButton, hasNewChatButton }) => ({
    button: {
      alignItems: 'center',
      color: theme.palette.primary.main,
      display: 'flex',
      gap: theme.spacing(0.5),
    },
    buttonText: {
      fontWeight: 600,
      whiteSpace: 'nowrap',
    },
    root: {
      alignItems: 'center',
      borderBottom: `1px solid ${theme.appColors.rule}`,
      display: 'grid',
      gap: theme.spacing(2),
      gridTemplateColumns: getGridTemplateColumns({ hasBackButton, hasNewChatButton }),
      padding: theme.spacing(0.5, 2),
    },
    title: {
      display: 'flex',
      justifyContent: 'center',
      minWidth: 0,
    },
  })
);

interface ChatPanelHeaderProps {
  isThreadNotFound: boolean;
  threadId: UUID;
  className?: string;
  onCreateNewChat?: () => void;
  onGoBack?: () => void;
}

export function ChatPanelHeader({
  className,
  isThreadNotFound,
  onCreateNewChat,
  onGoBack,
  threadId,
}: ChatPanelHeaderProps) {
  const hasBackButton = Boolean(onGoBack);
  const hasNewChatButton = Boolean(onCreateNewChat);
  const { classes } = useStyles({ hasBackButton, hasNewChatButton });
  const [setHeaderRef, headerMeasure] = useAlineMeasure<HTMLDivElement>();
  const headerWidth = headerMeasure?.width ?? 0;
  const showButtonText = headerWidth >= 500;

  const handleGoBack = () => {
    $resetChatThreadsState();
    onGoBack?.();
  };

  const handleNewChat = () => {
    $resetChatThreadsState();
    onCreateNewChat?.();
  };

  return (
    <div ref={setHeaderRef} className={clsx(classes.root, className)}>
      {hasBackButton && (
        <ButtonBase className={classes.button} sx={{ justifySelf: 'start' }} onClick={handleGoBack}>
          <ChevronLeft size={20} stroke="currentColor" />
          {showButtonText && (
            <Typography className={classes.buttonText} color="inherit">
              Go back
            </Typography>
          )}
        </ButtonBase>
      )}
      <div className={classes.title}>
        <ChatThreadTitle isThreadNotFound={isThreadNotFound} threadId={threadId} />
      </div>
      {hasNewChatButton && (
        <ButtonBase className={classes.button} sx={{ justifySelf: 'end' }} onClick={handleNewChat}>
          <SquarePen size={16} stroke="currentColor" />
          {showButtonText && (
            <Typography className={classes.buttonText} color="inherit">
              New Chat
            </Typography>
          )}
        </ButtonBase>
      )}
    </div>
  );
}

function getGridTemplateColumns({
  hasBackButton,
  hasNewChatButton,
}: {
  hasBackButton: boolean;
  hasNewChatButton: boolean;
}) {
  if (hasBackButton && hasNewChatButton) return 'minmax(0, 1fr) minmax(0, 2fr) minmax(0, 1fr)';
  if (hasBackButton) return 'auto minmax(0, 1fr)';
  if (hasNewChatButton) return 'minmax(0, 1fr) auto';
  return 'minmax(0, 1fr)';
}
